import pluralize from 'pluralize';
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router';

import {
  ALL_NUMERIC_DATE_FORMAT,
  AppRoutes,
  DEFAULT_LOCALE,
  DEFAULT_TIME_FORMAT,
} from '@/constants';
import { getDurationLabel, getTimezoneOffset } from '@/helpers';
import { navigate } from '@/providers';
import { mergeClasses } from '@/utils';

import {
  ChevronLeftIcon,
  CodeSnippet02Icon,
  Loading02Icon,
  RefreshCcw01Icon,
  XCloseIcon,
} from '@/assets/icons';
import {
  Badge,
  Button,
  IntakeJobExecutionCard,
  IntakeJobMetricsCard,
  Loading,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SvgIcon,
} from '@/components/common';

import { JobStatus } from '@/features/regulatory-intake/enums';
import {
  getJobStatusIcon,
  getJobStatusLabel,
  getJobStatusVariant,
  getTriggerLabel,
} from '@/features/regulatory-intake/utils';

import {
  useIntakeDetailsQuery,
  useJobRetryMutation,
} from '@/features/regulatory-intake/intake-details/hooks';

import MessagesSection from './MessagesSection';

const IntakeDetails = () => {
  const location = useLocation();
  const { id } = useParams();
  const { data, isFetching, error, refetch, isRefetching } = useIntakeDetailsQuery(id || '');
  const [jobLogOpen, setJobLogOpen] = useState(false);
  const [jobStatus, setJobStatus] = useState<JobStatus>(JobStatus.NotAvailable);

  const { mutate: jobRetry, isPending: isJobRetryPending } = useJobRetryMutation();

  useEffect(() => {
    if (data?.status) {
      setJobStatus(data.status);
    }
  }, [data, isRefetching]);

  if (error) {
    navigate(AppRoutes.INTERNAL_SERVER_ERROR, { replace: true });
    return null;
  }

  if (!id || (!isFetching && !data)) {
    navigate(AppRoutes.NOT_FOUND, { replace: true });
    return null;
  }

  if (isFetching || !data) {
    return <Loading fullScreen />;
  }

  return (
    <div>
      <div className="flex flex-col gap-4 pb-3">
        <Button
          buttonColor="neutral"
          buttonStyle="textlink"
          className="!text-neutral-7 self-start"
          prefixIcon={<ChevronLeftIcon />}
          onClick={() => {
            const hasHistory = location.key !== 'default' && window.history.length > 1;
            if (hasHistory) {
              window.history.back();
            } else {
              navigate(AppRoutes.INTAKE_JOBS);
            }
          }}
        >
          Back to Intake Jobs
        </Button>
        <div className="flex justify-between items-center">
          <div className="flex gap-4 items-center">
            <div className="text-h1 text-neutral-9 font-bold">{data.id}</div>
            <Badge
              variant={getJobStatusVariant(jobStatus)}
              size="default"
              className="items-center gap-2"
              mode="light"
              border
            >
              <span>
                <SvgIcon className="size-4" component={getJobStatusIcon(jobStatus)} />
              </span>
              {getJobStatusLabel(jobStatus)}
            </Badge>
          </div>
          {(data.processingLog || data.status === JobStatus.Failed) && (
            <div className="flex gap-2 items-center">
              {data.processingLog && (
                <Popover open={jobLogOpen} onOpenChange={setJobLogOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      active={jobLogOpen}
                      buttonColor="neutral"
                      buttonStyle="outline"
                      borderRadius="soft"
                      prefixIcon={<CodeSnippet02Icon />}
                    >
                      Job Log
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="end"
                    className="w-fit flex flex-col gap-3 bg-neutral-0 border border-neutral-4 shadow-[0px_0px_30px_0px_#57606D26] rounded-small p-3"
                  >
                    <div className="flex justify-between">
                      <div className="flex gap-2 items-center text-neutral-9">
                        <SvgIcon component={CodeSnippet02Icon} className="size-5" />
                        <div className="text-body-base font-semibold">Job Log</div>
                      </div>
                      <SvgIcon
                        className="size-6 text-neutral-6 cursor-pointer hover:text-neutral-7 transition-colors"
                        onClick={() => setJobLogOpen(false)}
                        component={XCloseIcon}
                      />
                    </div>
                    <div className="max-w-[536px] max-h-[50vh] overflow-auto p-3 bg-neutral-1 border border-neutral-4 rounded-xs text-body-sm text-neutral-7 font-medium break-words">
                      {data.processingLog}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              {data.status === JobStatus.Failed && (
                <Button
                  buttonColor="primary"
                  buttonStyle="filled"
                  borderRadius="soft"
                  prefixIcon={
                    <SvgIcon
                      component={isJobRetryPending ? Loading02Icon : RefreshCcw01Icon}
                      className={mergeClasses(
                        'size-6',
                        isJobRetryPending && 'animate-[spin_2s_linear_infinite]',
                      )}
                    />
                  }
                  onClick={() => jobRetry(id || '')}
                  disabled={isJobRetryPending}
                >
                  {isJobRetryPending ? 'Retrying Job...' : 'Retry Job'}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      {data.errorLog && (
        <div className="flex gap-2 text-body-sm font-medium">
          <div className="text-neutral-7">Failure reason:</div>
          <div className="text-neutral-9">{data.errorLog}</div>
        </div>
      )}
      {!!data.retry && data.retry > 0 && (
        <div className="text-neutral-7 text-body-sm font-medium mt-1">
          Retried {data.retry} {pluralize('time', data.retry)}.
        </div>
      )}
      <div className="py-3 flex gap-4">
        <IntakeJobExecutionCard
          title="Casa"
          trigger={getTriggerLabel(data.triggerMode)}
          startedBy={data.runBy || 'System'}
          lastJobDate={
            data.startTime
              ? new Date(data.startTime).toLocaleDateString(DEFAULT_LOCALE, {
                  ...ALL_NUMERIC_DATE_FORMAT,
                })
              : ''
          }
          nextJobDate={
            data.endTime
              ? new Date(data.endTime).toLocaleDateString(DEFAULT_LOCALE, {
                  ...ALL_NUMERIC_DATE_FORMAT,
                })
              : ''
          }
          lastJobTime={
            data.startTime
              ? new Date(data.startTime).toLocaleTimeString(DEFAULT_LOCALE, {
                  ...DEFAULT_TIME_FORMAT,
                })
              : ''
          }
          nextJobTime={
            data.endTime
              ? new Date(data.endTime).toLocaleTimeString(DEFAULT_LOCALE, {
                  ...DEFAULT_TIME_FORMAT,
                })
              : ''
          }
          className="w-1/3"
          duration={
            data.startTime && data.endTime
              ? getDurationLabel(new Date(data.startTime), new Date(data.endTime))
              : ''
          }
          timezone={getTimezoneOffset(new Date())}
        />
        <IntakeJobMetricsCard
          messagesRead={data.metrics?.messagesRead || 0}
          regulatoryNotifications={data.metrics?.regulatoryNotifications || 0}
          notRegulatoryNotifications={data.metrics?.notRegulatoryNotifications || 0}
          messagesFailed={data.metrics?.messagesFailed || 0}
        />
      </div>
      <MessagesSection
        jobId={id}
        jobStatus={data.status || JobStatus.NotAvailable}
        onJobStatusChange={setJobStatus}
        refetchJob={refetch}
      />
    </div>
  );
};

export default IntakeDetails;
